import React, { useState } from 'react';
import { format, parse, isValid, startOfDay } from 'date-fns';
import { Calendar as CalendarIcon, Clock, X } from 'lucide-react';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

const toDate = (v) => {
  if (!v) return undefined;
  const d = parse(String(v).slice(0, 10), 'yyyy-MM-dd', new Date());
  return isValid(d) ? d : undefined;
};

// Drop-in replacement for <Input type="date">: same value format (yyyy-MM-dd)
// and event-style onChange ({ target: { value } }), but opens a calendar popover.
export const DateInput = React.forwardRef(({ value, onChange, min, max, className, disabled, placeholder = 'Select date', required, ...props }, ref) => {
  const [open, setOpen] = useState(false);
  const selected = toDate(value);
  const minDate = toDate(min);
  const maxDate = toDate(max);
  const emit = (str) => onChange && onChange({ target: { value: str } });

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          ref={ref}
          disabled={disabled}
          className={cn(
            'flex h-9 w-full items-center gap-2 rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors text-left focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50',
            !selected && 'text-muted-foreground',
            className
          )}
          {...props}
        >
          <CalendarIcon className="w-4 h-4 shrink-0 opacity-60" />
          <span className="flex-1 truncate">{selected ? format(selected, 'dd/MM/yyyy') : placeholder}</span>
          {selected && !required && !disabled && (
            <X
              className="w-3.5 h-3.5 shrink-0 opacity-60 hover:opacity-100"
              data-testid="date-input-clear"
              onClick={(e) => { e.stopPropagation(); emit(''); }}
            />
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0 z-[9999]" align="start">
        <Calendar
          mode="single"
          captionLayout="dropdown-buttons"
          fromYear={2015}
          toYear={new Date().getFullYear() + 10}
          selected={selected}
          defaultMonth={selected || minDate}
          disabled={(d) =>
            (minDate && startOfDay(d) < startOfDay(minDate)) ||
            (maxDate && startOfDay(d) > startOfDay(maxDate))
          }
          onSelect={(d) => {
            if (d) emit(format(d, 'yyyy-MM-dd'));
            setOpen(false);
          }}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
});
DateInput.displayName = 'DateInput';

const fmt12 = (t) => {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
};

const HOURS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'));
const MINUTES = Array.from({ length: 12 }, (_, i) => String(i * 5).padStart(2, '0'));

const TimeSelects = ({ time, onTime }) => {
  const [h, m] = (time || '09:00').split(':');
  const minutes = MINUTES.includes(m) ? MINUTES : [...MINUTES, m].sort();
  const cls = 'h-8 rounded-md border border-input bg-background px-2 text-sm focus:outline-none';
  return (
    <div className="flex items-center gap-1.5">
      <select value={h} onChange={(e) => onTime(`${e.target.value}:${m}`)} className={cls} data-testid="time-hour-select">
        {HOURS.map((hh) => <option key={hh} value={hh}>{hh}</option>)}
      </select>
      <span className="text-sm text-muted-foreground">:</span>
      <select value={m} onChange={(e) => onTime(`${h}:${e.target.value}`)} className={cls} data-testid="time-minute-select">
        {minutes.map((mm) => <option key={mm} value={mm}>{mm}</option>)}
      </select>
      <span className="text-xs text-muted-foreground ml-1">{fmt12(`${h}:${m}`)}</span>
    </div>
  );
};

// Drop-in replacement for <Input type="time"> — value "HH:mm".
export const TimeInput = React.forwardRef(({ value, onChange, className, disabled, required, placeholder = 'Time', ...props }, ref) => {
  const [open, setOpen] = useState(false);
  const emit = (str) => onChange && onChange({ target: { value: str } });
  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (o && !value) emit('09:00');
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          ref={ref}
          disabled={disabled}
          className={cn(
            'flex h-9 w-full items-center gap-2 rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors text-left focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50',
            !value && 'text-muted-foreground',
            className
          )}
          {...props}
        >
          <Clock className="w-4 h-4 shrink-0 opacity-60" />
          <span className="flex-1 truncate">{value ? fmt12(value) : placeholder}</span>
          {value && !required && !disabled && (
            <X
              className="w-3.5 h-3.5 shrink-0 opacity-60 hover:opacity-100"
              data-testid="time-input-clear"
              onClick={(e) => { e.stopPropagation(); emit(''); }}
            />
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-3 z-[9999]" align="start">
        <TimeSelects time={value || '09:00'} onTime={(t) => emit(t)} />
      </PopoverContent>
    </Popover>
  );
});
TimeInput.displayName = 'TimeInput';

// Drop-in replacement for <Input type="datetime-local"> — value "yyyy-MM-ddTHH:mm".
export const DateTimeInput = React.forwardRef(({ value, onChange, min, max, className, disabled, required, placeholder = 'Select date & time', ...props }, ref) => {
  const [open, setOpen] = useState(false);
  const v = value ? String(value) : '';
  const dateStr = v.slice(0, 10);
  const timeStr = v.length >= 16 ? v.slice(11, 16) : '09:00';
  const selected = toDate(dateStr);
  const minDate = toDate(min ? String(min).slice(0, 10) : '');
  const maxDate = toDate(max ? String(max).slice(0, 10) : '');
  const emit = (d, t) => onChange && onChange({ target: { value: d ? `${d}T${t}` : '' } });
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          ref={ref}
          disabled={disabled}
          className={cn(
            'flex h-9 w-full items-center gap-2 rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors text-left focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50',
            !selected && 'text-muted-foreground',
            className
          )}
          {...props}
        >
          <CalendarIcon className="w-4 h-4 shrink-0 opacity-60" />
          <span className="flex-1 truncate">
            {selected ? `${format(selected, 'dd/MM/yyyy')}, ${fmt12(timeStr)}` : placeholder}
          </span>
          {selected && !required && !disabled && (
            <X
              className="w-3.5 h-3.5 shrink-0 opacity-60 hover:opacity-100"
              data-testid="datetime-input-clear"
              onClick={(e) => { e.stopPropagation(); emit('', ''); }}
            />
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0 z-[9999]" align="start">
        <Calendar
          mode="single"
          captionLayout="dropdown-buttons"
          fromYear={2015}
          toYear={new Date().getFullYear() + 10}
          selected={selected}
          defaultMonth={selected || minDate}
          disabled={(d) =>
            (minDate && startOfDay(d) < startOfDay(minDate)) ||
            (maxDate && startOfDay(d) > startOfDay(maxDate))
          }
          onSelect={(d) => { if (d) emit(format(d, 'yyyy-MM-dd'), timeStr); }}
          initialFocus
        />
        <div className="flex items-center justify-between gap-3 border-t border-border p-3">
          <TimeSelects time={timeStr} onTime={(t) => emit(dateStr || format(new Date(), 'yyyy-MM-dd'), t)} />
          <button
            type="button"
            className="h-8 px-3 rounded-md bg-primary text-primary-foreground text-sm"
            onClick={() => setOpen(false)}
            data-testid="datetime-done-btn"
          >
            Done
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
});
DateTimeInput.displayName = 'DateTimeInput';

export default DateInput;
